import type { DownloadItineraryOptions } from "@/components";
import { DownloadItineraryModal, ProductStatusBadge } from "@/components";
import { FileDownload } from "@/components/FileDownload";
import { Alert, Anchor, App as AntApp, Button, Empty, Image, Skeleton } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import {
  ArrowLeftIcon,
  BedDoubleIcon,
  BusIcon,
  CalendarDaysIcon,
  Clock3Icon,
  CopyPlusIcon,
  DownloadIcon,
  Edit3Icon,
  MapPinIcon,
  PackagePlusIcon,
  PlusIcon,
  RouteIcon,
  SendIcon,
  TentTreeIcon,
  UsersRoundIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router";
import {
  downloadPortalItinerary,
  PortalActivityDetail,
  PortalHotelDetail,
  PortalTransportDetail,
  useDuplicatePortalItineraryMutation,
  usePortalItineraryDetailQuery,
  usePortalItineraryStatusQuery,
  usePortalQuoteMarkupQuery,
  useSavePortalQuoteMarkupMutation,
  useSubmitPortalItineraryMutation,
} from "../actions/itinerary.actions";
import { useEditQuote } from "../hooks/useEditQuote";
import { QuoteMarkupModal } from "../components/QuoteMarkupModal";
import { QuotePricingCard } from "../components/QuotePricingCard";
import { PortalPageHeader, PortalPageShell } from "../components/PortalProduct";
import type { PortalWorkspaceContext } from "../components/PortalWorkspaceLayout";
import { draftToMarkup, markupToDraft, type MarkupDraft } from "../lib/quoteMarkup";
import { getPortalPaths } from "../lib/routes";

const statusTone = (key: string): "gold" | "neutral" | "success" | "danger" => {
  if (key === "confirmed" || key === "completed") return "success";
  if (key === "cancelled") return "danger";
  if (key === "submitted" || key === "in_progress") return "gold";
  return "neutral";
};

const formatDate = (value: string | null) => (value ? dayjs(value).format("DD MMM YYYY") : "Not set");

const getRequestError = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError<{ error?: string }>(error)) return fallback;
  return error.response?.data?.error || fallback;
};

const ProductImage = ({ src, alt, fallback }: { src: string | null; alt: string; fallback: string }) => (
  <div className="shrink-0">
    <Image
      width={112}
      height={86}
      preview={false}
      src={src || undefined}
      fallback={fallback}
      alt={alt}
      className="rounded-lg border border-gray-200 object-cover"
    />
  </div>
);

const HotelCard = ({ hotel }: { hotel: PortalHotelDetail }) => (
  <article className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row">
    <ProductImage src={hotel.image} alt={hotel.name} fallback="/images/no-image.jpg" />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-[#131B2A]">{hotel.name}</h3>
          <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
            <MapPinIcon size={12} /> {[hotel.city, hotel.country].filter(Boolean).join(", ") || "Location not set"}
          </p>
        </div>
        {hotel.operationalStatus ? (
          <ProductStatusBadge tone={statusTone(hotel.operationalStatus.key)}>
            {hotel.operationalStatus.label}
          </ProductStatusBadge>
        ) : null}
      </div>
      {hotel.description ? <p className="mt-3 text-sm leading-6 text-gray-600">{hotel.description}</p> : null}
      <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2 lg:grid-cols-4">
        <span>
          {formatDate(hotel.checkInDate)} – {formatDate(hotel.checkOutDate)}
        </span>
        <span>{hotel.roomType || "Room type not set"}</span>
        <span>{hotel.mealPlan || "Meal plan not set"}</span>
        <span>
          {hotel.numberOfRooms} room{hotel.numberOfRooms === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  </article>
);

const TransportCard = ({ item }: { item: PortalTransportDetail }) => (
  <article className="flex gap-3 rounded-lg border border-gray-200 bg-white p-3">
    <ProductImage src={item.image} alt={item.name} fallback="/images/no-image.jpg" />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="font-semibold text-[#131B2A]">{item.name}</h4>
        {item.operationalStatus ? (
          <ProductStatusBadge tone={statusTone(item.operationalStatus.key)}>
            {item.operationalStatus.label}
          </ProductStatusBadge>
        ) : null}
      </div>
      {item.description ? <p className="mt-2 text-sm leading-6 text-gray-600">{item.description}</p> : null}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        {item.startTime ? (
          <span className="flex items-center gap-1">
            <Clock3Icon size={12} /> {item.startTime}
          </span>
        ) : null}
        {item.durationHours ? <span>{item.durationHours} hours</span> : null}
        {item.selectedVehicleType ? (
          <span>
            {item.selectedVehicleType}
            {item.vehiclePax ? ` · ${item.vehiclePax} seats` : ""}
          </span>
        ) : null}
        <span>Quantity {item.quantity}</span>
      </div>
    </div>
  </article>
);

const ActivityCard = ({ item }: { item: PortalActivityDetail }) => (
  <article className="flex gap-3 rounded-lg border border-gray-200 bg-white p-3">
    <ProductImage src={item.image} alt={item.name} fallback="/images/activity.jpg" />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="font-semibold text-[#131B2A]">{item.name}</h4>
        {item.operationalStatus ? (
          <ProductStatusBadge tone={statusTone(item.operationalStatus.key)}>
            {item.operationalStatus.label}
          </ProductStatusBadge>
        ) : null}
      </div>
      {item.description ? <p className="mt-2 text-sm leading-6 text-gray-600">{item.description}</p> : null}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        {item.category ? <span>{item.category}</span> : null}
        {item.duration ? <span>{item.duration} hours</span> : null}
        {item.selectedVehicleType ? (
          <span>
            {item.selectedVehicleType}
            {item.vehiclePax ? ` · ${item.vehiclePax} seats` : ""}
          </span>
        ) : null}
        <span>Quantity {item.quantity}</span>
      </div>
    </div>
  </article>
);

/** Section header matching the itinerary workspace cards (icon tile, title, one-line subtitle). */
type PortalDay = { transports: PortalTransportDetail[]; activities: PortalActivityDetail[] };
type PortalDayService = { kind: "transport"; item: PortalTransportDetail } | { kind: "activity"; item: PortalActivityDetail };

/** True when the day's services were rearranged in the daily plan (any has a `dayOrder`). */
const hasDayOrder = (day: PortalDay) => [...day.transports, ...day.activities].some((item) => typeof item.dayOrder === "number");

/** The day's transports and activities by `dayOrder`; services without one follow, transports first. */
const orderedDayServices = (day: PortalDay): PortalDayService[] =>
  [
    ...day.transports.map((item, index) => ({ service: { kind: "transport" as const, item }, legacy: index })),
    ...day.activities.map((item, index) => ({ service: { kind: "activity" as const, item }, legacy: day.transports.length + index })),
  ]
    .sort((left, right) => {
      const leftOrder = left.service.item.dayOrder;
      const rightOrder = right.service.item.dayOrder;
      const leftHas = typeof leftOrder === "number";
      const rightHas = typeof rightOrder === "number";
      if (leftHas !== rightHas) return leftHas ? -1 : 1;
      if (leftHas && rightHas && leftOrder !== rightOrder) return (leftOrder as number) - (rightOrder as number);
      return left.legacy - right.legacy;
    })
    .map(({ service }) => service);

const SectionHeader = ({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle: string }) => (
  <div className="flex min-h-14 items-center gap-3 border-b border-gray-200 px-4 py-3">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-(--brand-primary)/25 bg-(--brand-primary)/10">
      {icon}
    </span>
    <div>
      <h2 className="text-sm font-semibold text-[#131B2A]">{title}</h2>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
  </div>
);

const anchorTitle = (icon: ReactNode, label: string) => (
  <span className="flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-[#FBFBFB] hover:text-[#131B2A]">
    {icon} {label}
  </span>
);

/** Renders one portal-safe itinerary with agency-facing operational status. */
const PortalItineraryDetailPage = () => {
  const { id = "" } = useParams();
  const { organisationName } = useOutletContext<PortalWorkspaceContext>();
  const navigate = useNavigate();
  const { message, modal } = AntApp.useApp();
  const [downloading, setDownloading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const query = usePortalItineraryDetailQuery(id);
  const statusQuery = usePortalItineraryStatusQuery(id);
  const submitMutation = useSubmitPortalItineraryMutation();
  const markupQuery = usePortalQuoteMarkupQuery(id);
  const saveMarkupMutation = useSavePortalQuoteMarkupMutation();
  const [markupModalOpen, setMarkupModalOpen] = useState(false);
  const duplicateMutation = useDuplicatePortalItineraryMutation();
  const { startEdit, modalHolder: editQuoteModal } = useEditQuote();
  const portalPaths = getPortalPaths();
  const itinerary = query.data;
  const requestStatus = statusQuery.data?.requestStatus || itinerary?.requestStatus;
  const bookingStatus = statusQuery.data?.bookingStatus || itinerary?.bookingStatus;

  const download = async (options: DownloadItineraryOptions) => {
    if (!itinerary || downloading) return;
    try {
      setDownloading(true);
      setDownloadModalOpen(false);
      const response = await downloadPortalItinerary(itinerary.id, options);
      const disposition = String(response.headers["content-disposition"] || "");
      const filename =
        disposition.match(/filename="?([^"]+)"?/i)?.[1] ||
        `itinerary-${itinerary.reference.replace("#", "")}.${options.format === "docx" ? "docx" : "pdf"}`;
      FileDownload(response.data, filename);
      message.success("Itinerary downloaded");
    } catch (error) {
      message.error(getRequestError(error, "Unable to download the itinerary"));
    } finally {
      setDownloading(false);
    }
  };

  if (query.isLoading) {
    return (
      <PortalPageShell>
        <div className="space-y-4">
          <Skeleton active />
          <Skeleton active />
        </div>
      </PortalPageShell>
    );
  }

  if (query.isError || !itinerary) {
    const notFound = axios.isAxiosError(query.error) && query.error.response?.status === 404;
    return (
      <PortalPageShell width="max-w-4xl">
        <Alert
          type="error"
          showIcon
          message={notFound ? "Itinerary not found" : "Unable to load itinerary"}
          description={
            notFound ? "This request is unavailable or does not belong to your agency." : "Please try again."
          }
          action={
            !notFound ? (
              <Button size="small" onClick={() => query.refetch()}>
                Retry
              </Button>
            ) : undefined
          }
        />
      </PortalPageShell>
    );
  }

  const hasDayContent = itinerary.days.some((day) => day.transports.length || day.activities.length);
  const totalPrice = statusQuery.data?.totalPrice || itinerary.totalPrice;
  const pricingTable = markupQuery.data?.pricing;
  const priceRow = (key: string) => pricingTable?.rows.find((row) => row.key === key)?.yourPrice;
  const supplierTotal = priceRow("total") ?? null;
  const perPerson = pricingTable
    ? { adult: priceRow("perAdult") ?? 0, cwb: priceRow("perChildWithBed") ?? 0, cnb: priceRow("perChildWithoutBed") ?? 0, infant: priceRow("perInfant") ?? 0 }
    : undefined;
  const childPrice = priceRow("perChildWithBed") ?? priceRow("perChildWithoutBed");

  const submitDraft = () =>
    modal.confirm({
      title: `Confirm Quote ${itinerary.optionNumber}?`,
      content: `This sends the quote to ${organisationName}. You will not be able to edit any quote in this trip unless ${organisationName} returns it.`,
      okText: "Confirm Quote",
      cancelText: "Keep editing",
      onOk: async () => {
        try {
          await submitMutation.mutateAsync(itinerary.id);
          message.success(`Quote confirmed with ${organisationName}.`);
          navigate(portalPaths.request(itinerary.leadId));
        } catch (error) {
          message.error(getRequestError(error, "Unable to confirm the quote"));
        }
      },
    });

  const openMarkupModal = () => setMarkupModalOpen(true);

  const saveMarkup = async (draft: MarkupDraft | null) => {
    try {
      await saveMarkupMutation.mutateAsync({ itineraryId: itinerary.id, markup: draft ? draftToMarkup(draft) : null });
      setMarkupModalOpen(false);
      message.success(draft ? "Markup saved. Your downloads include it." : "Markup removed.");
    } catch (error) {
      message.error(getRequestError(error, "Unable to save the markup"));
    }
  };

  const duplicateDraft = async () => {
    try {
      const copy = await duplicateMutation.mutateAsync(itinerary.id);
      message.success("Alternative created.");
      navigate(portalPaths.editItinerary(copy.id));
    } catch (error) {
      message.error(getRequestError(error, "Unable to duplicate the itinerary"));
    }
  };

  return (
    <PortalPageShell>
      <div className="space-y-3">
        <Link
          to={portalPaths.request(itinerary.leadId)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 no-underline hover:text-(--brand-primary)"
        >
          <ArrowLeftIcon size={14} />
          {itinerary.guestName || "Unnamed guest"} · Request {itinerary.reference}
        </Link>
        <PortalPageHeader
          title={`Quote ${itinerary.optionNumber}`}
          description={`Request ${itinerary.reference} · ${itinerary.destinations.join(", ")}`}
          meta={
            <span className="flex flex-wrap gap-2">
              {requestStatus ? (
                <ProductStatusBadge tone={statusTone(requestStatus.key)}>{requestStatus.label}</ProductStatusBadge>
              ) : null}
              {bookingStatus ? (
                <ProductStatusBadge tone={statusTone(bookingStatus.key)}>Booking: {bookingStatus.label}</ProductStatusBadge>
              ) : null}
            </span>
          }
        />

        {itinerary.portalReturnReason ? (
          <Alert
            type="warning"
            showIcon
            message={`Returned by ${organisationName}`}
            description={itinerary.portalReturnReason}
          />
        ) : null}

        <section
          id="trip-details"
          aria-label="Trip details summary"
          className="scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-(--brand-primary) text-[var(--brand-primary-foreground)]">
                <RouteIcon className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">Route</p>
                <p className="max-w-[34rem] truncate text-sm font-semibold text-[#131B2A]">
                  {itinerary.destinations.join(" · ") || "Not set"}
                </p>
              </div>
            </div>
            <span className="hidden h-8 w-px bg-gray-200 sm:block" aria-hidden="true" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">Travel dates</p>
              <p className="text-sm font-medium tabular-nums text-[#131B2A]">
                {formatDate(itinerary.startDate)} – {formatDate(itinerary.endDate)} · {itinerary.nights} nights
              </p>
            </div>
            <div className="flex items-center gap-2">
              <UsersRoundIcon className="h-4 w-4 text-(--brand-primary)" aria-hidden="true" />
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">Guests</p>
                <p className="text-sm font-medium text-[#131B2A]">{itinerary.travellers.summary}</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">Selling currency</p>
              <p className="text-sm font-medium text-[#131B2A]">{pricingTable?.currency || totalPrice.currency}</p>
            </div>
          </div>
          {itinerary.requestNotes ? (
            <div className="border-t border-gray-200 px-4 py-3">
              <p className="text-xs font-medium text-gray-500">Request notes</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-600">{itinerary.requestNotes}</p>
            </div>
          ) : null}
        </section>

        <div className="grid gap-4 lg:grid-cols-12">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-9">
            <Anchor
              offsetTop={64}
              targetOffset={128}
              bounds={8}
              affix
              direction="horizontal"
              className="sticky z-20 overflow-x-auto rounded-xl border border-gray-200 bg-white !py-0 shadow-[0_1px_2px_rgba(19,27,42,0.04)] [&_.ant-anchor]:!min-w-max [&_.ant-anchor-ink-ball]:!border-(--brand-primary) [&_.ant-anchor-link-title-active]:!text-[#131B2A]"
              style={{ marginBlockStart: 0 }}
              items={[
                { key: "trip-details", href: "#trip-details", title: anchorTitle(<CalendarDaysIcon className="mr-2 h-4 w-4 text-(--brand-primary)" />, "Trip details") },
                { key: "hotels", href: "#hotels", title: anchorTitle(<BedDoubleIcon className="mr-2 h-4 w-4 text-(--brand-primary)" />, "Hotels") },
                { key: "daily-plan", href: "#daily-plan", title: anchorTitle(<BusIcon className="mr-2 h-4 w-4 text-(--brand-primary)" />, "Daily plan") },
              ]}
            />

            <section id="hotels" className="scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
              <SectionHeader icon={<BedDoubleIcon className="h-4 w-4 text-(--brand-primary)" />} title="Hotels" subtitle={`${itinerary.hotels.length} accommodation stay${itinerary.hotels.length === 1 ? "" : "s"}`} />
              <div className="space-y-3 p-4">
                {itinerary.hotels.length ? (
                  itinerary.hotels.map((hotel, index) => (
                    <HotelCard key={`${hotel.id || hotel.name}-${index}`} hotel={hotel} />
                  ))
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hotels included" />
                )}
              </div>
            </section>

            <section id="daily-plan" className="scroll-mt-32 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
              <SectionHeader icon={<TentTreeIcon className="h-4 w-4 text-(--brand-primary)" />} title="Daily plan" subtitle="Activities and ground transport in one timeline" />
              {!hasDayContent ? (
                <div className="p-6">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No day-by-day services included" />
                </div>
              ) : null}
              <div className="divide-y divide-gray-200">
                {itinerary.days.map((day) => (
                  <article key={day.day} className="grid gap-4 p-4 md:grid-cols-[110px_minmax(0,1fr)]">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-(--brand-primary)">Day {day.day + 1}</p>
                      <h3 className="font-semibold text-[#131B2A]">{formatDate(day.date)}</h3>
                    </div>
                    <div className="min-w-0 space-y-3">
                      {hasDayOrder(day) ? (
                        // A day rearranged in the daily plan lists its services in that order.
                        <div className="space-y-2">
                          {orderedDayServices(day).map((service, index) =>
                            service.kind === "transport" ? (
                              <TransportCard key={`t-${service.item.id || service.item.name}-${index}`} item={service.item} />
                            ) : (
                              <ActivityCard key={`a-${service.item.id || service.item.name}-${index}`} item={service.item} />
                            ),
                          )}
                        </div>
                      ) : null}
                      {!hasDayOrder(day) && day.transports.length ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                            <BusIcon size={14} /> Transports
                          </div>
                          {day.transports.map((item, index) => (
                            <TransportCard key={`${item.id || item.name}-${index}`} item={item} />
                          ))}
                        </div>
                      ) : null}
                      {!hasDayOrder(day) && day.activities.length ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                            <TentTreeIcon size={14} /> Activities
                          </div>
                          {day.activities.map((item, index) => (
                            <ActivityCard key={`${item.id || item.name}-${index}`} item={item} />
                          ))}
                        </div>
                      ) : null}
                      {!day.transports.length && !day.activities.length ? (
                        <p className="text-xs text-gray-500">Nothing planned for this day.</p>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>

        {itinerary.travel?.length ? <section className="rounded-xl border border-gray-200 bg-white p-4"><h2>Flights</h2>{itinerary.travel.map((flight, index) => <article key={index}><p>{flight.source} to {flight.destination} ? {flight.airline} {flight.flightNumber}</p>{flight.connections.map((connection, position) => <p key={position}>Connection: {connection.source} to {connection.destination} ? {connection.airline} {connection.flightNumber}</p>)}</article>)}</section> : null}
        {itinerary.optionalOffers?.map((offer) => <section key={offer.key} className="rounded-xl border border-gray-200 bg-white p-4"><h3>{offer.name}</h3><p>{offer.priceLabel}</p><p>{offer.description}</p></section>)}
        {itinerary.customServices?.length ? (
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
            <div className="mb-3 flex items-center gap-2"><PackagePlusIcon size={17} className="text-(--brand-primary)" /><h2 className="font-semibold text-[#131B2A]">Additional Services</h2></div>
            <div className="divide-y divide-gray-200">
              {itinerary.customServices.map((service, index) => (
                <article key={index} className="py-4 first:pt-0 last:pb-0">
                  <h3 className="break-words font-semibold text-[#131B2A]">{service.name}</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-500">{[service.category, service.timing, service.location].filter(Boolean).join(" · ")}</p>
                  {service.description ? <p className="mt-3 whitespace-pre-line break-words text-sm leading-6 text-gray-600">{service.description}</p> : null}
                  {service.fields.length ? <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                    {service.fields.map((field, fieldIndex) => <div key={fieldIndex} className="min-w-0"><dt className="text-xs font-medium text-gray-500">{field.label}</dt><dd className="mt-1 whitespace-pre-line break-words text-sm text-[#131B2A]">{field.value}</dd></div>)}
                  </dl> : null}
                  {service.inclusions.length || service.exclusions.length ? <div className="mt-3 space-y-2 text-sm leading-6 text-gray-600">
                    {service.inclusions.length ? <p><span className="font-medium text-[#131B2A]">Included: </span>{service.inclusions.join(", ")}</p> : null}
                    {service.exclusions.length ? <p><span className="font-medium text-[#131B2A]">Excluded: </span>{service.exclusions.join(", ")}</p> : null}
                  </div> : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}
          </div>

          <aside aria-label="Quote pricing and actions" className="flex min-w-0 flex-col gap-4 lg:col-span-3">
            <QuotePricingCard
              organisationName={organisationName}
              currency={pricingTable?.currency || totalPrice.currency}
              supplierTotal={markupQuery.isError ? totalPrice.amount : supplierTotal}
              calculating={markupQuery.isLoading}
              adultCount={itinerary.travellers.adults}
              supplierPassengers={pricingTable?.supplierPassengers}
              childCosts={pricingTable?.rows.filter((row) => row.key.startsWith("child:")).map((row) => ({ id: row.key, label: row.label, amount: row.yourPrice }))}
              perPerson={perPerson}
              childPrice={childPrice}
              markupDraft={markupToDraft(markupQuery.data?.markup)}
              onEditMarkup={openMarkupModal}
            />
            <section aria-label="Actions" className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
              <h2 className="text-sm font-semibold text-[#131B2A]">Actions</h2>
              <div className="mt-3 flex flex-col gap-2">
                <Button
                  type="primary"
                  icon={<DownloadIcon size={15} />}
                  loading={downloading}
                  disabled={!itinerary.downloadAvailable || downloading}
                  onClick={() => setDownloadModalOpen(true)}
                >
                  Download itinerary
                </Button>
                {itinerary.canEdit ? (
                  <Button icon={<Edit3Icon size={15} />} onClick={() => startEdit(itinerary.id)}>
                    Edit
                  </Button>
                ) : null}
                {itinerary.canEdit ? (
                  <Button icon={<CopyPlusIcon size={15} />} loading={duplicateMutation.isPending} onClick={duplicateDraft}>
                    Duplicate quote
                  </Button>
                ) : null}
                {itinerary.canEdit ? (
                  <Button icon={<PlusIcon size={15} />} onClick={() => navigate(portalPaths.newRequestOption(itinerary.leadId))}>
                    Blank quote
                  </Button>
                ) : null}
                {itinerary.canSubmit ? (
                  <Button type="primary" icon={<SendIcon size={15} />} loading={submitMutation.isPending} onClick={submitDraft}>
                    Confirm Quote
                  </Button>
                ) : null}
              </div>
            </section>
          </aside>
        </div>

        <footer className="flex flex-wrap justify-between gap-2 border-t border-gray-200 pt-4 text-xs text-gray-500">
          <span>Confirmed {formatDate(itinerary.submittedAt)}</span>
          <span>Last updated {formatDate(statusQuery.data?.updatedAt || itinerary.updatedAt)}</span>
        </footer>
        <QuoteMarkupModal
          open={markupModalOpen}
          initial={markupToDraft(markupQuery.data?.markup)}
          optionalOffers={itinerary.optionalOffers}
          currency={markupQuery.data?.pricing.currency || totalPrice.currency}
          supplierTotal={markupQuery.data?.pricing.rows.find((row) => row.key === "total")?.yourPrice ?? null}
          supplierTax={markupQuery.data?.pricing.rows.find((row) => row.key === "taxes")?.yourPrice}
          organisationName={organisationName}
          canRemove={Boolean(markupQuery.data?.markup)}
          saving={saveMarkupMutation.isPending}
          onCancel={() => setMarkupModalOpen(false)}
          onSave={(draft) => saveMarkup(draft)}
          onRemove={() => saveMarkup(null)}
        />
        {editQuoteModal}
        <DownloadItineraryModal
          open={downloadModalOpen}
          onCancel={() => setDownloadModalOpen(false)}
          onDownload={download}
          agentMode
        />
      </div>
    </PortalPageShell>
  );
};

export default PortalItineraryDetailPage;
